import React from 'react';
import { Avatar, Box, Button, Card, CardContent, Chip, Skeleton, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { Groups, Lock, Public } from '@mui/icons-material';
import { ICommunity } from '../../../interfaces/ICommunityHub';
import { useApiClient } from '../../../ApiClientProvider';
import { isProtectedCommunityImageRoute } from '../../../utils/communityImageUtils';
import { getSafeHexColour } from '../../../utils/colourUtils';

export interface CommunityCardProps {
    community: ICommunity;
    onClick: () => void;
    onJoin?: () => void;
    /** Shows a disabled "Joining…" state on the join button while a join request is in flight. */
    joining?: boolean;
    /** Smaller image/typography scale for tight grids (e.g. "for you" suggestions). Ignored in the `row` variant. */
    dense?: boolean;
    /** `card` (default) is the image-banner tile used on discover/search grids; `row` is a bordered row for sidebar lists; `organiser` is a brand-coloured, image-free tile for an organiser's own community listing. */
    variant?: 'card' | 'row' | 'organiser';
    /** `row` variant only — trailing slot, e.g. a joined-state indicator. */
    action?: React.ReactNode;
    /** `row` variant only — avatar size in px. */
    avatarSize?: number;
    /** `row` variant only — overrides the default "N members" caption. */
    subtitle?: string;
    /** `row` variant only — removes the container border for lightweight sidebar lists. */
    borderless?: boolean;
    /** `organiser` variant only — overrides theme colours with the organiser's brand colours. */
    brandPrimary?: string;
    /** `organiser` variant only — overrides theme colours with the organiser's brand colours. */
    brandSecondary?: string;
}

/** Community tile/row — an image-banner card for grids (`variant="card"`, default) or a bordered row with an optional logo for sidebar lists (`variant="row"`). */
export const CommunityCard: React.FC<CommunityCardProps> = ({
    community, onClick, onJoin, joining = false, dense = false,
    variant = 'card', action, avatarSize = 40, subtitle, borderless = false, brandPrimary, brandSecondary,
}) => {
    const theme = useTheme();
    const apiClient = useApiClient('applicationClient');
    const focalPosition = `${community.imageFocalPointX ?? 50}% ${community.imageFocalPointY ?? 50}%`;

    // Private community images are served from a membership-gated endpoint that can't be
    // used directly in an <img>/CSS background — resolve it to a blob URL instead.
    const rawImageUrl = community.imageUrl ?? null;
    const isProtectedRoute = isProtectedCommunityImageRoute(rawImageUrl);
    const [resolvedProtectedImage, setResolvedProtectedImage] = React.useState<{ source: string; url: string } | null>(null);
    const rawLogoUrl = community.logoUrl ?? null;
    const isProtectedLogoRoute = isProtectedCommunityImageRoute(rawLogoUrl);
    const [resolvedProtectedLogo, setResolvedProtectedLogo] = React.useState<{ source: string; url: string } | null>(null);

    React.useEffect(() => {
        if (!isProtectedRoute || !rawImageUrl) return;

        let isActive = true;
        let objectUrl: string | null = null;

        (async () => {
            try {
                const response = await apiClient.get<Blob>(rawImageUrl, { responseType: 'blob' });
                const url = URL.createObjectURL(response.data);
                if (isActive) {
                    objectUrl = url;
                    setResolvedProtectedImage({ source: rawImageUrl, url });
                } else {
                    // Effect was cleaned up while the request was in flight — revoke immediately, nothing will reference it.
                    URL.revokeObjectURL(url);
                }
            } catch {
                if (isActive) setResolvedProtectedImage(null);
            }
        })();

        return () => {
            isActive = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [apiClient, isProtectedRoute, rawImageUrl]);

    React.useEffect(() => {
        if (!isProtectedLogoRoute || !rawLogoUrl) return;

        let isActive = true;
        let objectUrl: string | null = null;

        (async () => {
            try {
                const response = await apiClient.get<Blob>(rawLogoUrl, { responseType: 'blob' });
                const url = URL.createObjectURL(response.data);
                if (isActive) {
                    objectUrl = url;
                    setResolvedProtectedLogo({ source: rawLogoUrl, url });
                } else {
                    URL.revokeObjectURL(url);
                }
            } catch {
                if (isActive) setResolvedProtectedLogo(null);
            }
        })();

        return () => {
            isActive = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [apiClient, isProtectedLogoRoute, rawLogoUrl]);

    const displayImageUrl = isProtectedRoute
        ? (resolvedProtectedImage?.source === rawImageUrl ? resolvedProtectedImage.url : undefined)
        : (rawImageUrl ?? undefined);
    const displayLogoUrl = isProtectedLogoRoute
        ? (resolvedProtectedLogo?.source === rawLogoUrl ? resolvedProtectedLogo.url : undefined)
        : (rawLogoUrl ?? undefined);
    const logoBadgeSize = dense ? 44 : 56;
    const ownerPrimary = getSafeHexColour(community.ownerCompanyPrimaryColour, theme.palette.primary.main);
    const ownerSecondary = getSafeHexColour(community.ownerCompanySecondaryColour, theme.palette.secondary.main);
    const organiserDescription = community.description || 'No description added yet.';
    const cardDescription = community.description ?? 'Connect with people who share your interests.';

    if (variant === 'row') {
        const caption = subtitle ?? `${community.memberCount.toLocaleString()} member${community.memberCount === 1 ? '' : 's'}`;

        return (
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1.5}
                onClick={onClick}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onClick(); } }}
                sx={{
                    px: 1,
                    py: 0.85,
                    borderRadius: 1.5,
                    border: borderless ? 'none' : '1px solid',
                    borderColor: borderless ? 'transparent' : 'divider',
                    bgcolor: borderless ? 'transparent' : 'background.paper',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s, border-color 0.15s',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03), borderColor: alpha(theme.palette.primary.main, 0.18) },
                    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0, flex: 1 }}>
                    <Avatar
                        variant="rounded"
                        src={displayLogoUrl}
                        alt={`${community.name} logo`}
                        sx={{
                            width: avatarSize,
                            height: avatarSize,
                            background: displayLogoUrl ? undefined : `linear-gradient(135deg, ${ownerSecondary}, ${ownerPrimary})`,
                            color: displayLogoUrl ? undefined : theme.palette.getContrastText(ownerPrimary),
                        }}
                    >
                        {community.name.trim().charAt(0).toLocaleUpperCase('en-GB')}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{community.name}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">{caption}</Typography>
                    </Box>
                </Stack>
                {action && <Box onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>{action}</Box>}
            </Stack>
        );
    }

    if (variant === 'organiser') {
        const primary = brandPrimary ?? theme.palette.primary.main;
        const secondary = brandSecondary ?? theme.palette.secondary.main;
        const isPublic = community.visibility === 'Public';

        return (
            <Card
                variant="outlined"
                onClick={onClick}
                sx={{
                    p: dense ? 1.5 : 2,
                    borderRadius: 2,
                    height: '100%',
                    borderColor: alpha(primary, 0.16),
                    background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    '&:hover': { borderColor: alpha(primary, 0.4) },
                }}
            >
                <Typography variant={dense ? 'subtitle2' : 'subtitle1'} sx={{ fontWeight: 800, lineHeight: 1.3, mb: 0.5 }}>
                    {community.name}
                </Typography>

                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={0.4} alignItems="center">
                        {isPublic
                            ? <Public sx={{ fontSize: 13, color: 'text.secondary' }} />
                            : <Lock sx={{ fontSize: 13, color: 'text.secondary' }} />
                        }
                        <Typography variant="caption" color="text.secondary">{community.visibility}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.4} alignItems="center">
                        <Groups sx={{ fontSize: 13, color: secondary }} />
                        <Typography variant="caption" sx={{ color: secondary }}>{community.memberCount.toLocaleString()} members</Typography>
                    </Stack>
                </Stack>

                <Tooltip title={organiserDescription} arrow>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            flexGrow: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: dense ? 2 : 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                            lineHeight: 1.6,
                            mb: 1.5,
                        }}
                    >
                        {organiserDescription}
                    </Typography>
                </Tooltip>

                <Box>
                    <Button
                        variant={community.isMember ? 'outlined' : 'contained'}
                        size="small"
                        onClick={(event) => { event.stopPropagation(); onJoin?.(); }}
                        disabled={community.isMember || !community.isJoinable || joining}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '999px',
                            minWidth: 120,
                            ...(community.isMember ? {
                                borderColor: alpha(primary, 0.3),
                                color: primary,
                            } : {
                                bgcolor: primary,
                                color: '#fff',
                                '&:hover': { bgcolor: secondary },
                            }),
                        }}
                    >
                        {community.isMember ? 'Member' : joining ? 'Joining…' : 'Join'}
                    </Button>
                </Box>
            </Card>
        );
    }

    return (
        <Card
            variant="outlined"
            onClick={onClick}
            sx={{
                height: '100%',
                minHeight: dense ? 176 : 202,
                borderRadius: 2,
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'box-shadow 0.15s, transform 0.15s',
                '&:hover': { boxShadow: theme.shadows[3], transform: 'translateY(-1px)' },
            }}
        >
            <Box
                sx={{
                    height: dense ? 84 : 108,
                    position: 'relative',
                    background: displayImageUrl
                        ? `url(${displayImageUrl}) ${focalPosition}/cover`
                        : `linear-gradient(135deg, ${ownerSecondary}, ${ownerPrimary})`,
                }}
            >
                {displayLogoUrl && (
                    <Avatar
                        variant="rounded"
                        src={displayLogoUrl}
                        alt={`${community.name} logo`}
                        sx={{
                            position: 'absolute',
                            left: dense ? 12 : 20,
                            bottom: -(logoBadgeSize / 2),
                            width: logoBadgeSize,
                            height: logoBadgeSize,
                            bgcolor: 'background.paper',
                            color: 'text.primary',
                            border: '3px solid',
                            borderColor: 'background.paper',
                            boxShadow: theme.shadows[2],
                            fontWeight: 700,
                        }}
                    />
                )}
            </Box>
            <CardContent sx={{ p: dense ? 2 : 2.5, pt: displayLogoUrl ? (dense ? 4 : 5) : (dense ? 2 : 2.5) }}>
                <Typography variant={dense ? 'subtitle2' : 'subtitle1'} noWrap>{community.name}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">{community.memberCount.toLocaleString()} members · by {community.ownerCompanyName ?? 'AttendIn'}</Typography>
                <Tooltip title={cardDescription} arrow>
                    <Typography
                        variant={dense ? 'caption' : 'body2'}
                        color={dense ? 'text.secondary' : alpha(theme.palette.primary.main, 0.72)}
                        sx={{
                            mt: dense ? 1 : 1.5,
                            minHeight: dense ? 32 : 40,
                            display: '-webkit-box',
                            overflow: 'hidden',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}
                    >
                        {cardDescription}
                    </Typography>
                </Tooltip>
                {community.isMember ? (
                    <Chip label="Joined" size="small" color="success" sx={{ mt: 1.25, bgcolor: 'success.lighter', color: 'success.dark' }} />
                ) : onJoin ? (
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={joining}
                        onClick={(event) => { event.stopPropagation(); onJoin(); }}
                        sx={{ mt: 1.25, borderRadius: 999 }}
                    >
                        {joining ? 'Joining…' : 'Join'}
                    </Button>
                ) : null}
            </CardContent>
        </Card>
    );
};

export interface CommunityCardSkeletonProps {
    /** Matches the `dense` sizing used by the real card. */
    dense?: boolean;
}

/** Placeholder matching CommunityCard's shape, shown while a communities list/grid is loading. */
export const CommunityCardSkeleton: React.FC<CommunityCardSkeletonProps> = ({ dense = false }) => (
    <Card variant="outlined" sx={{ height: '100%', minHeight: dense ? 176 : 202, borderRadius: 2, overflow: 'hidden' }}>
        <Skeleton variant="rectangular" height={dense ? 84 : 108} />
        <CardContent sx={{ p: dense ? 2 : 2.5 }}>
            <Skeleton variant="text" width="70%" sx={{ fontSize: dense ? '0.95rem' : '1.05rem' }} />
            <Skeleton variant="text" width="50%" sx={{ fontSize: '0.75rem' }} />
            <Skeleton variant="text" width="90%" sx={{ mt: dense ? 1 : 1.5 }} />
            <Skeleton variant="rounded" width={72} height={28} sx={{ mt: 1.25, borderRadius: 999 }} />
        </CardContent>
    </Card>
);

export default CommunityCard;
